import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, AlertCircle, Copy, CheckCircle2, X, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';

/**
 * OverrideRequestCircle — sits above the email thread Reply bar when
 * the thread carries an `identity_help_request_token`. Renders a
 * spinning red circle that the supervisor can expand into the full
 * "Request Admin Override" flow (or the pending/approved/rejected
 * status follow-ups depending on the linked override_request row).
 *
 * States:
 *   • idle        → red spinning circle. Click → expand inline button.
 *                   Inline button click → identity-verify popup.
 *   • pending     → yellow circle (Admin Override Pending).
 *                   Click → reveals the wait-for-admin popup.
 *   • approved    → yellow circle (Override Issued). Click → reveals
 *                   plaintext override password + countdown.
 *   • rejected    → red circle but with X cross-out. Click → shows
 *                   admin rationale + ability to re-request.
 *   • consumed    → unmounts.
 */
export default function OverrideRequestCircle({ threadId, api, currentUserId }) {
  const [ctx, setCtx] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!threadId) return;
    try {
      const r = await api.get(`/email/threads/${threadId}/override-context`);
      setCtx(r.data);
    } catch (e) {
      setCtx({ exists: false });
    } finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [threadId]);

  // Auto-refresh every 15s when in pending state to catch approval/rejection
  useEffect(() => {
    if (!ctx?.exists) return;
    const s = ctx?.status;
    if (s === 'request_pending' || s === 'request_approved') {
      const t = setInterval(load, 15000);
      return () => clearInterval(t);
    }
    return undefined;
  }, [ctx?.status, ctx?.exists]); // eslint-disable-line

  if (loading || !ctx?.exists) return null;
  if (ctx.status === 'consumed') return null;

  const status = ctx.status;
  // Status → colour + heading
  const isRejected = status === 'request_rejected';
  const isPending = status === 'request_pending';
  const isApproved = (status === 'request_approved') && !!ctx.override_password;
  const circleClass = isApproved || isPending
    ? 'bg-yellow-500'
    : 'bg-red-500';
  const circleAriaLabel = isApproved ? 'Admin override approved' : isPending ? 'Admin override pending' : isRejected ? 'Admin override rejected' : 'Request admin override';

  return (
    <>
      {/* Circle — fixed right side just above reply area */}
      <div
        className="fixed right-3 z-40 flex items-center gap-2"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 9rem)' }}
        data-testid="override-circle-anchor"
      >
        {expanded ? (
          <button
            onClick={() => { setExpanded(false); setPopupOpen(true); }}
            className={`px-3 py-2 rounded-full font-semibold text-white text-xs shadow-lg ${circleClass}`}
            data-testid="override-circle-expanded-btn"
          >
            {isApproved ? 'View Override' : isPending ? 'Admin Override Pending' : isRejected ? 'Override Rejected' : 'Request Admin Override?'}
          </button>
        ) : (
          <button
            onClick={() => setExpanded(true)}
            aria-label={circleAriaLabel}
            className={`relative w-11 h-11 rounded-full ${circleClass} text-white shadow-xl flex items-center justify-center hover:scale-110 transition-transform`}
            data-testid="override-circle"
          >
            <span className={`absolute inset-0 rounded-full border-2 border-white/50 ${(isApproved || isRejected) ? '' : 'animate-spin border-t-transparent'}`} />
            <AlertCircle className="w-5 h-5 relative z-10" />
          </button>
        )}
      </div>

      {/* Popup */}
      <Dialog open={popupOpen} onOpenChange={setPopupOpen}>
        <DialogContent className="max-w-md" data-testid="override-popup">
          {isApproved ? (
            <ApprovedReveal ctx={ctx} api={api} onClose={() => setPopupOpen(false)} onReload={load} />
          ) : isPending ? (
            <PendingView ctx={ctx} onClose={() => setPopupOpen(false)} />
          ) : isRejected ? (
            <RejectedView ctx={ctx} onClose={() => setPopupOpen(false)} onReload={load} />
          ) : (
            <RequestForm ctx={ctx} api={api} onClose={() => setPopupOpen(false)} onReload={load} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─────────────────── RequestForm (initial verify identity) ───────────────────

function RequestForm({ ctx, api, onClose, onReload }) {
  const [certified, setCertified] = useState(false);
  const [mfa, setMfa] = useState(true);
  const [recovery, setRecovery] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const requester = ctx.requester || {};
  const userLabel = requester.role === 'lead' ? "lead's" : "affiliate's";

  const submit = async (verified) => {
    if (!verified) { onClose(); return; }
    if (!certified) { toast.error('You must certify the identity check'); return; }
    if (!mfa && !recovery) { toast.error('Select at least one credential to override'); return; }
    setSubmitting(true);
    try {
      await api.post('/override-requests', {
        help_request_id: ctx.help_request_id,
        bypass_mfa: mfa, bypass_recovery_key: recovery,
        supervisor_certified: true,
      });
      toast.success('Override request submitted');
      onClose();
      await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Request Admin Override?</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">
          Before you may request an Admin Override code, you need to personally verify this {userLabel} identity. Their registered contact information is:
        </p>
        <Card>
          <CardContent className="p-3 text-xs space-y-1.5">
            <KV label="Name" value={requester.name} />
            <KV label="Phone Number" value={requester.phone} icon="phone" contactName={requester.name} />
            <KV label="Email Address" value={requester.email} icon="email" contactName={requester.name} />
            <KV label="ID Number" value={requester.id_number} />
          </CardContent>
        </Card>
        <p className="text-xs text-muted-foreground">
          When you contact <span className="font-semibold">{requester.name || 'this user'}</span>, make them verify their State Issued ID number as confirmation of their identity. The provided number MUST match the number above.
        </p>
        <label className="flex items-start gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={certified}
            onChange={(e) => setCertified(e.target.checked)}
            className="mt-0.5"
            data-testid="override-certify-checkbox"
          />
          <span>I certify that I've personally verified this person's identity as <span className="font-semibold">{requester.name}</span>.</span>
        </label>
        <div className="space-y-1.5">
          <p className="text-xs font-medium">Which credentials does {requester.name} need overridden?</p>
          <div className="flex gap-2 text-xs">
            <button onClick={() => { setMfa(true); setRecovery(true); }} className="px-2 py-1 text-[11px] rounded bg-muted hover:bg-muted/80" data-testid="override-select-both">
              Select all
            </button>
          </div>
          <div className="flex gap-3 text-xs">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={mfa} onChange={(e) => setMfa(e.target.checked)} data-testid="override-mfa-checkbox" /> MFA
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={recovery} onChange={(e) => setRecovery(e.target.checked)} data-testid="override-recovery-checkbox" /> Recovery Key
            </label>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={() => submit(false)} disabled={submitting} data-testid="override-not-verified-btn">
            Not Verified
          </Button>
          <Button size="sm" className="bg-[#586D52] hover:bg-[#4a5d46]" onClick={() => submit(true)} disabled={submitting || !certified} data-testid="override-request-btn">
            {submitting ? 'Submitting…' : 'Request Admin Override'}
          </Button>
        </div>
      </div>
    </>
  );
}

// ─────────────────── PendingView ───────────────────

function PendingView({ ctx, onClose }) {
  const requester = ctx.requester || {};
  return (
    <>
      <DialogHeader>
        <DialogTitle>Admin Override Pending</DialogTitle>
      </DialogHeader>
      <div className="space-y-2 text-sm">
        <p className="text-xs text-muted-foreground">
          Your request for an override has been submitted. If approved, an Admin will provide you a single use override password for <span className="font-semibold">{requester.name}</span> to use upon their next login attempt. The override password will expire 60 minutes after issuance.
        </p>
        <p className="text-xs text-muted-foreground">
          Please be prepared to provide <span className="font-semibold">{requester.name}</span> with the override password as soon as you receive it. <span className="font-semibold">{requester.name}</span> will need to login with the password before the password expires.
        </p>
        <Button size="sm" variant="outline" className="w-full" onClick={onClose} data-testid="override-pending-close">
          Got it
        </Button>
      </div>
    </>
  );
}

// ─────────────────── ApprovedReveal ───────────────────

function ApprovedReveal({ ctx, api, onClose, onReload }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [remaining, setRemaining] = useState(ctx.bypass_remaining_seconds || 0);
  useEffect(() => {
    if (!remaining) return undefined;
    const t = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [remaining > 0]); // eslint-disable-line
  const requester = ctx.requester || {};
  const factors = ctx.override_request?.factors || {};
  const factorLabel = factors.mfa && factors.recovery_key
    ? 'MFA & Recovery Key'
    : factors.mfa ? 'MFA' : factors.recovery_key ? 'Recovery Key' : 'credentials';
  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  return (
    <>
      <DialogHeader>
        <DialogTitle>Admin Override Approved</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 text-sm">
        <p className="text-xs">
          An admin has overridden <span className="font-semibold">{requester.name}</span>'s {factorLabel} credentials. <span className="font-semibold">{requester.name}</span> will need to login with the admin override password.
        </p>
        <p className="text-xs text-muted-foreground">
          Please contact <span className="font-semibold">{requester.name}</span> immediately. Tell them to click "Forgot Password", enter their email address, and then type in the override password along with any additional details requested.
        </p>
        <p className="text-xs text-muted-foreground">
          This override will expire in: <span className="font-mono font-bold text-red-600">{mm}:{ss}</span>
        </p>
        <Card>
          <CardContent className="p-3 text-xs space-y-1.5">
            <KV label="Name" value={requester.name} />
            <KV label="Phone Number" value={requester.phone} icon="phone" contactName={requester.name} />
            <KV label="Email Address" value={requester.email} icon="email" contactName={requester.name} />
            <KV label="ID Number" value={requester.id_number} />
          </CardContent>
        </Card>
        {!revealed ? (
          <Button size="sm" className="w-full bg-[#586D52] hover:bg-[#4a5d46]" onClick={() => setRevealed(true)} data-testid="override-reveal-btn">
            Reveal Override
          </Button>
        ) : (
          <div className="rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 text-center space-y-2">
            <p className="font-mono font-bold text-lg break-all" data-testid="override-password-revealed">{ctx.override_password}</p>
            <button
              onClick={() => { navigator.clipboard.writeText(ctx.override_password); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              className="text-[11px] text-amber-700 dark:text-amber-300 inline-flex items-center gap-1 underline"
              data-testid="override-password-copy"
            >
              {copied ? <><CheckCircle2 className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
            </button>
          </div>
        )}
        <Button size="sm" variant="outline" className="w-full" onClick={onClose}>
          Close
        </Button>
      </div>
    </>
  );
}

// ─────────────────── RejectedView ───────────────────

function RejectedView({ ctx, onClose, onReload }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Admin Override Rejected</DialogTitle>
      </DialogHeader>
      <div className="space-y-2 text-sm">
        <p className="text-xs">
          The admin has rejected the override request. Please review their rationale below and re-submit a new request if appropriate.
        </p>
        <Card>
          <CardContent className="p-3 text-xs italic" data-testid="override-rejection-rationale">
            {ctx.rejection_rationale || ctx.override_request?.admin_rejection_rationale || '(No rationale provided)'}
          </CardContent>
        </Card>
        <Button size="sm" variant="outline" className="w-full" onClick={async () => { await onReload(); onClose(); }} data-testid="override-rejected-close">
          OK
        </Button>
      </div>
    </>
  );
}

// ─────────────────── KV row helper ───────────────────

function KV({ label, value, icon, contactName }) {
  const dispatchSheet = (mode) => {
    if (!value) return;
    window.dispatchEvent(new CustomEvent('open-contact-sheet', {
      detail: { mode, value, contactName },
    }));
  };
  const dt = label.toLowerCase().replace(/\s+/g, '-');
  return (
    <div className="flex items-start gap-2">
      <span className="text-muted-foreground w-24 shrink-0">{label}</span>
      <span className="flex-1 font-medium break-all flex items-center gap-1.5">
        <span data-testid={`override-kv-${dt}`}>
          {value || <span className="text-muted-foreground italic">Not on file</span>}
        </span>
        {value && icon === 'phone' && (
          <button onClick={() => dispatchSheet('call')} aria-label="Call" className="ml-1 p-1 rounded hover:bg-muted" data-testid={`override-call-${dt}`}>
            <Phone className="w-3.5 h-3.5 text-[#586D52]" />
          </button>
        )}
        {value && icon === 'email' && (
          <button onClick={() => dispatchSheet('email')} aria-label="Email" className="ml-1 p-1 rounded hover:bg-muted" data-testid={`override-email-${dt}`}>
            <Mail className="w-3.5 h-3.5 text-[#586D52]" />
          </button>
        )}
      </span>
    </div>
  );
}
